import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { withStyles } from '@mui/styles';

import { StoreUtil, DateUtil, QueryUtil, DevRequestUtil } from 'doctivity-shared/utils';

import { DynamicTable } from 'components';

import { queryDevRequests, updateDevRequest } from 'store/actions/activityLogsActions';

import { withRouter } from 'utils';
import axiosInstance from 'utils/axiosUtil';

/**
 * Admin-only list of dev requests raised from the Key AI chat.
 *
 * Reached by typing /devrequests — deliberately NOT linked from the side menu (see
 * AppNavigation.getAdminItems). The route itself is admin-gated in AppRoot.
 *
 * Rows are ActivityLogs of type AI_DEVREQUEST; the row holds a pointer and the full
 * diagnostic payload lives in S3, fetched on demand through a short-lived presigned URL.
 * Status/priority are plain sortable columns; clicking a row opens a dialog that shows
 * every field, edits status/priority, and views/downloads the full context.
 */

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  statusFilter: {
    minWidth: 180,
    marginBottom: theme.spacing(2),
  },
  // Dialog: two-column definition list of read-only fields.
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  fieldValue: {
    fontSize: 13,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  editRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  dialogSelect: {
    minWidth: 170,
  },
  objectDisplay: {
    fontSize: 12,
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 320,
    overflow: 'auto',
    background: 'rgba(0,0,0,0.03)',
    padding: theme.spacing(1),
    borderRadius: 4,
  },
  downloadRow: {
    marginTop: theme.spacing(1),
  },
  error: {
    fontSize: 12,
    color: theme.palette.error.main,
  },
});

// Header status-filter values that aren't a single status: 'active' (the default) hides
// archived requests; 'all' shows everything. Any other value is a literal status string.
const STATUS_FILTER_ACTIVE = 'active';
const STATUS_FILTER_ALL = 'all';

// Translate the header dropdown selection into an ActivityLogs.status `where` clause
// (merged onto the base { type } filter). Returns undefined to mean "no status filter".
const statusWhereFor = (filterValue) => {
  if (filterValue === STATUS_FILTER_ALL) return undefined;
  if (filterValue === STATUS_FILTER_ACTIVE) return { $in: DevRequestUtil.ACTIVE_STATUSES };
  return filterValue;
};

const priorityLabel = (p) => {
  if (p === 1 || p === '1') return '1 (highest)';
  if (p === 5 || p === '5') return '5 (lowest)';
  return p != null && p !== '' ? String(p) : '';
};

const prettyPrint = (value) => JSON.stringify(value, null, 2);

const formatBytes = (bytes) => {
  if (bytes == null || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Safe read of the JSON blob in ActivityLogs.context. Older or truncated rows may not
// parse — show the raw string rather than blanking the row.
const parseContext = (row) => {
  if (!row?.context) return null;
  try {
    return JSON.parse(row.context);
  } catch (e) {
    return { unparsed_context: row.context };
  }
};

const columns = [
  {
    label: 'Date & Time',
    key: 'created_at',
    format: DateUtil.formatDateTimeFromDB,
    style: { width: 150 },
  },
  {
    label: 'Status',
    key: 'status',
    // Real column -> sortable ORDER BY. Filtered via the header dropdown, not a text box.
    showFilter: false,
    format: (value) => DevRequestUtil.statusLabel(value),
    style: { width: 120 },
  },
  {
    label: 'Priority',
    key: 'priority',
    // 1 = highest. Real column -> sortable.
    showFilter: false,
    format: (value) => priorityLabel(value),
    style: { width: 100 },
  },
  {
    label: 'Category',
    // Derived from the JSON in `context`; the key is a display-only identifier and is
    // not a column on the row (format reads the parsed context instead). It must stay
    // unique — DynamicTable uses column.key as a React key.
    key: 'category',
    // Neither sorting nor filtering can be pushed down to SQL meaningfully here —
    // a LIKE against the raw JSON blob would only confuse.
    sortable: false,
    showFilter: false,
    format: (context, row) => {
      const parsed = parseContext(row);
      return parsed?.category ? DevRequestUtil.categoryLabel(parsed.category) : '';
    },
    style: { width: 170 },
  },
  {
    label: 'Description',
    key: 'description',
  },
  {
    label: 'Errors',
    key: 'server_error_count',
    sortable: false,
    showFilter: false,
    // Backend AI_ERROR count. A non-zero value usually means the cause is already in
    // the file — worth seeing before deciding which request to open.
    format: (context, row) => {
      const parsed = parseContext(row);
      return parsed?.server_error_count ? String(parsed.server_error_count) : '';
    },
    style: { width: 70 },
  },
  {
    label: 'Client',
    key: 'selected_client.name',
    style: { width: 125 },
  },
  {
    label: 'User',
    key: 'user_id',
    // Same filter behavior as the Activity Log page: type a name or an id.
    filterFields: ['user.last_name', 'user.first_name', 'user.id'],
    format: (userId, row) => (row.user ? `${row.user.first_name} ${row.user.last_name}` : ''),
    style: { width: 150 },
  },
];

class DevRequestsPage extends React.Component {
  constructor(props) {
    super(props);
    // Default view is the working queue: everything except archived.
    this.state = {
      statusFilter: STATUS_FILTER_ACTIVE,
      query: {
        // Only AI_DEVREQUEST rows — this shares the /ActivityLogs endpoint with the
        // Activity Log page, so the type filter is what makes it a distinct view.
        where: {
          type: DevRequestUtil.ACTIVITY_TYPE,
          status: statusWhereFor(STATUS_FILTER_ACTIVE),
        },
        include: [
          {
            association: 'user',
          },
          {
            association: 'selected_client',
            attributes: ['id', 'name'],
          },
        ],
        order: [['created_at', 'DESC']],
        limit: 25,
        offset: 0,
      },
      // The row whose detail/edit dialog is open, or null. Holds a local copy so optimistic
      // status/priority edits show immediately while the table reloads underneath.
      selectedRow: null,
      // Full S3 context file for the open dialog: pretty-printed string once loaded.
      fullContext: null,
      // null | 'loading' | { error } for the inline full-context fetch.
      fullContextState: null,
      // reference -> 'loading' | { error } while a presigned URL is being fetched
      downloading: {},
    };
  }

  componentDidMount() {
    const { devRequests } = this.props;

    if (StoreUtil.needsLoadNoCache(devRequests)) {
      this.fetchData();
    }
  }

  fetchData() {
    const { dispatch } = this.props;
    const { query } = this.state;

    // Promote the `user` include to an INNER JOIN while a name/id filter is active,
    // matching the Activity Log page.
    QueryUtil.promoteFilteredIncludes(query.include);

    dispatch(queryDevRequests(query));
  }

  onQueryChange = (query) => {
    this.setState({ query }, this.fetchData.bind(this));
  };

  // Header dropdown: rewrite the status clause on the base { type } filter and reload.
  onStatusFilterChange = (value) => {
    this.setState(
      (prev) => {
        const where = { ...prev.query.where };
        const statusWhere = statusWhereFor(value);
        if (statusWhere === undefined) {
          delete where.status;
        } else {
          where.status = statusWhere;
        }
        return {
          statusFilter: value,
          query: { ...prev.query, where, offset: 0 },
        };
      },
      this.fetchData.bind(this),
    );
  };

  onRowClick = (row) => {
    this.setState({ selectedRow: row, fullContext: null, fullContextState: null });
    const context = parseContext(row);
    if (context?.s3_key) {
      this.loadFullContext(row, context);
    }
  };

  closeDialog = () => {
    this.setState({ selectedRow: null, fullContext: null, fullContextState: null });
  };

  // Pull the full diagnostic file (the rich JSON that Download returns) through our own
  // authed API and show it inline. `inline=1` makes DevRequestFile return the body itself
  // rather than a presigned S3 URL, so this doesn't depend on the bucket's CORS config.
  loadFullContext = async (row, context) => {
    const key = context?.s3_key;
    if (!key) return;

    this.setState({ fullContextState: 'loading' });

    try {
      const response = await axiosInstance.get('/DevRequestFile', { params: { key, inline: 1 } });
      const data = response?.data;
      // Too big to round-trip through the API — fall back to Download (streams from S3).
      if (data?.tooLarge) {
        this.setState((prev) => (
          prev.selectedRow && prev.selectedRow.id === row.id
            ? { fullContextState: { tooLarge: true, size: data.size } }
            : null
        ));
        return;
      }
      const content = data?.content;
      if (content == null) throw new Error('No content returned.');
      let pretty = content;
      try {
        pretty = prettyPrint(JSON.parse(content));
      } catch (e) {
        // Not JSON (or truncated) — show it raw.
      }
      // Ignore the result if the dialog has since moved to another row or closed.
      this.setState((prev) => (
        prev.selectedRow && prev.selectedRow.id === row.id
          ? { fullContext: pretty, fullContextState: null }
          : null
      ));
    } catch (e) {
      this.setState((prev) => (
        prev.selectedRow && prev.selectedRow.id === row.id
          ? { fullContextState: { error: e?.message || 'Could not load full context.' } }
          : null
      ));
    }
  };

  // Edit the open row's status/priority. Optimistically update the dialog, PUT it via the
  // admin-only /DevRequestUpdate endpoint, then reload the table so sorting and the status
  // filter reflect the change (e.g. archiving drops the row from the default "active" view).
  // Revert the dialog value if the save fails.
  saveField = (field, value) => {
    const { selectedRow } = this.state;
    if (!selectedRow || value === selectedRow[field]) return;
    const prevValue = selectedRow[field];
    const rowId = selectedRow.id;

    this.setState({ selectedRow: { ...selectedRow, [field]: value } });

    const { dispatch } = this.props;
    Promise.resolve(dispatch(updateDevRequest({ id: rowId, [field]: value })))
      .then(() => this.fetchData())
      .catch(() => {
        // Roll back to what the server still has — only if the same row is still open.
        this.setState((prev) => (
          prev.selectedRow && prev.selectedRow.id === rowId
            ? { selectedRow: { ...prev.selectedRow, [field]: prevValue } }
            : null
        ));
      });
  };

  // Fetch a short-lived presigned URL, then open it. The link is not embedded in the
  // page because it expires in 15 minutes — generating it on click keeps it valid.
  onDownload = async (row, context) => {
    const key = context?.s3_key;
    const reference = context?.reference || row.id;
    if (!key) return;

    this.setState((prev) => ({ downloading: { ...prev.downloading, [reference]: 'loading' } }));

    try {
      // Direct call rather than a redux action — this is a one-off URL with nothing to
      // cache, matching how document downloads work elsewhere (ProviderCommentDetailPage).
      const response = await axiosInstance.get('/DevRequestFile', { params: { key } });
      const url = response?.data?.url;
      if (!url) throw new Error('No download URL returned.');
      // Anchor-click rather than window.open: survives popup blockers after an await.
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.setState((prev) => {
        const next = { ...prev.downloading };
        delete next[reference];
        return { downloading: next };
      });
    } catch (e) {
      this.setState((prev) => ({
        downloading: { ...prev.downloading, [reference]: { error: e?.message || 'Download failed.' } },
      }));
    }
  };

  renderDialog() {
    const { classes } = this.props;
    const { selectedRow: row, downloading, fullContext, fullContextState } = this.state;
    if (!row) return null;

    const context = parseContext(row);
    const reference = context?.reference || row.id;
    const dlState = downloading[reference];
    const userName = row.user ? `${row.user.first_name} ${row.user.last_name}` : '';
    const category = context?.category ? DevRequestUtil.categoryLabel(context.category) : '';

    const fields = [
      ['Date & Time', DateUtil.formatDateTimeFromDB(row.created_at)],
      ['Category', category],
      ['Client', row.selected_client?.name || ''],
      ['User', userName],
      ['Errors', context?.server_error_count ? String(context.server_error_count) : '0'],
      ['Reference', String(reference)],
    ];

    return (
      <Dialog open onClose={this.closeDialog} maxWidth='md' fullWidth>
        <DialogTitle>Dev Request</DialogTitle>
        <DialogContent dividers>
          <div className={classes.editRow}>
            <FormControl size='small' className={classes.dialogSelect}>
              <InputLabel id='devrequest-dialog-status'>Status</InputLabel>
              <Select
                labelId='devrequest-dialog-status'
                label='Status'
                value={row.status ?? ''}
                onChange={(e) => this.saveField('status', e.target.value)}
              >
                {DevRequestUtil.STATUS_ORDER.map((s) => (
                  <MenuItem key={s} value={s}>
                    {DevRequestUtil.statusLabel(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size='small' className={classes.dialogSelect}>
              <InputLabel id='devrequest-dialog-priority'>Priority</InputLabel>
              <Select
                labelId='devrequest-dialog-priority'
                label='Priority'
                value={row.priority ?? ''}
                onChange={(e) => this.saveField('priority', e.target.value)}
              >
                {DevRequestUtil.PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {priorityLabel(p)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={classes.fieldGrid}>
            {fields.map(([label, value]) => (
              <React.Fragment key={label}>
                <Typography className={classes.fieldLabel}>{label}</Typography>
                <Typography className={classes.fieldValue}>{value || '—'}</Typography>
              </React.Fragment>
            ))}
          </div>

          <Typography className={classes.sectionTitle}>Description</Typography>
          <Typography className={classes.fieldValue}>{row.description || '—'}</Typography>

          <Typography className={classes.sectionTitle}>Full context</Typography>
          {context?.s3_key ? (
            <>
              <div className={classes.downloadRow}>
                <Button
                  size='small'
                  variant='outlined'
                  disabled={dlState === 'loading'}
                  onClick={() => this.onDownload(row, context)}
                >
                  {dlState === 'loading' ? 'Preparing…' : 'Download full context'}
                </Button>
                {dlState && dlState.error && (
                  <Typography className={classes.error}>{dlState.error}</Typography>
                )}
              </div>
              {fullContextState === 'loading' && (
                <Typography className={classes.fieldValue}>Loading full context…</Typography>
              )}
              {fullContext && <pre className={classes.objectDisplay}>{fullContext}</pre>}
              {fullContextState && fullContextState.tooLarge && (
                <Typography className={classes.fieldValue}>
                  {`Full context is too large to display here (${formatBytes(fullContextState.size)}). Use Download to view it.`}
                </Typography>
              )}
              {fullContextState && fullContextState.error && (
                <Typography className={classes.error}>{fullContextState.error}</Typography>
              )}
            </>
          ) : (
            <Typography className={classes.error}>
              {context?.store_error
                ? `Context file was not saved: ${context.store_error}`
                : 'No context file for this request.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { devRequests, classes } = this.props;
    const { query, statusFilter } = this.state;

    return (
      <>
        <title>Dev Requests</title>
        <div className={classes.container}>
          <Card>
            <CardContent>
              <DynamicTable
                header={
                  <div className={classes.tableHeader}>
                    <Typography
                      component='h2'
                      variant='h6'
                      color='primary'
                      gutterBottom
                    >
                      Dev Requests
                    </Typography>
                    <FormControl size='small' className={classes.statusFilter}>
                      <InputLabel id='devrequest-status-filter-label'>Status</InputLabel>
                      <Select
                        labelId='devrequest-status-filter-label'
                        label='Status'
                        value={statusFilter}
                        onChange={(e) => this.onStatusFilterChange(e.target.value)}
                      >
                        <MenuItem value={STATUS_FILTER_ACTIVE}>Active (not archived)</MenuItem>
                        <MenuItem value={STATUS_FILTER_ALL}>All</MenuItem>
                        {DevRequestUtil.STATUS_ORDER.map((s) => (
                          <MenuItem key={s} value={s}>
                            {DevRequestUtil.statusLabel(s)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                }
                columns={columns}
                colcount={columns.length}
                data={devRequests}
                ContainerType={React.Fragment}
                query={query}
                onQueryChange={this.onQueryChange}
                onRowClick={this.onRowClick}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </CardContent>
          </Card>
        </div>
        {this.renderDialog()}
      </>
    );
  }
}

DevRequestsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  devRequests: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const { activityLogs } = state;

  return {
    devRequests: StoreUtil.get(activityLogs, 'devrequests_table'),
  };
}

const styled = withStyles(styles)(DevRequestsPage);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as DevRequestsPage };
